/**
 * BlockedAction — wraps an interactive UI element with block-policy enforcement.
 *
 * Reads from BlockPolicyContext. If the user is blocked from `action`, the
 * children are rendered visually disabled (dimmed + cursor-not-allowed + red
 * "lock" badge in the corner) and clicks/hovers are intercepted so the user
 * sees the policy message instead of the underlying action firing.
 *
 * Usage:
 *
 *   <BlockedAction action="upload_document">
 *     <button onClick={handleUpload}>Upload</button>
 *   </BlockedAction>
 *
 *   // Hide-instead-of-dim variant
 *   <BlockedAction action="download" mode="hide">
 *     <a href="...">Download</a>
 *   </BlockedAction>
 *
 *   // Inline (no overlay; just a tooltip + disabled style)
 *   <BlockedAction action="view" mode="inline">
 *     <button>View</button>
 *   </BlockedAction>
 *
 * Props:
 *   - action:   'upload_document' | 'upload_voice' | 'upload_text'
 *               | 'download' | 'view' | 'stream_voice'  (required)
 *   - mode:     'overlay' (default) — wraps in a relative div, renders a red
 *               lock badge over the corner of the child and intercepts clicks
 *               'inline'  — passes `disabled` to the child and shows a tooltip
 *               'hide'    — renders nothing when blocked
 *   - children: the action UI (button, link, drag-drop zone, etc.)
 *   - tooltipSide: 'top' | 'bottom' | 'left' | 'right' (default 'top')
 *   - badgeSize: 'sm' | 'md' (default 'sm')
 *   - className: extra classes for the outer wrapper
 *
 * Why no separate "BlockedButton": wrapping is more flexible — works with
 * <button>, <a>, <div onClick>, and complex composites like drag-drop zones
 * without forcing every callsite to swap component identity.
 */

import { cloneElement, isValidElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HiOutlineLockClosed, HiOutlineArrowRight } from 'react-icons/hi2'
import { useActionGate } from '../../contexts/BlockPolicyContext'

const SEVERITY_BADGE = {
  error:   { ring: 'ring-red-500', bg: 'bg-danger-soft0',     text: 'text-white',  pill: 'bg-danger-soft text-danger border-danger/30' },
  warning: { ring: 'ring-amber-500', bg: 'bg-warning-soft0', text: 'text-white',  pill: 'bg-warning-soft text-warning border-warning/30' },
  info:    { ring: 'ring-blue-500', bg: 'bg-info-soft0',   text: 'text-white',  pill: 'bg-info-soft text-brand border-info/30' },
}

export default function BlockedAction({
  action,
  mode = 'overlay',
  tooltipSide = 'top',
  badgeSize = 'sm',
  className = '',
  children,
}) {
  const gate = useActionGate(action)
  const navigate = useNavigate()
  const [hovering, setHovering] = useState(false)

  // While the policy is still loading we render children unchanged — better
  // than briefly flashing a blocked state for an action the user can actually
  // perform. The server-side check still enforces.
  if (gate.loading || gate.allowed) return children

  if (mode === 'hide') return null

  const severity = SEVERITY_BADGE[gate.severity] || SEVERITY_BADGE.error
  const message = gate.message || 'This action is not available right now.'
  const ctaLabel = ctaLabelFor(gate.hint_action)
  const ctaUrl = gate.cta_url

  const handleBlockedClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (ctaUrl) navigate(ctaUrl)
  }

  // ─── Inline mode: pass `disabled` + onClick interceptor to the child ──
  // Layout-transparent (display:contents) so it doesn't shrink `w-full`
  // buttons or break flex/grid sizing.
  if (mode === 'inline' && isValidElement(children)) {
    const child = cloneElement(children, {
      disabled: true,
      'aria-disabled': true,
      title: message,
      onClick: handleBlockedClick,
      className: `${children.props.className || ''} opacity-50 cursor-not-allowed`.trim(),
    })
    return child
  }

  // ─── Overlay mode (default) ───────────────────────────────────────────
  const badgeDim = badgeSize === 'md' ? 'w-6 h-6' : 'w-4 h-4'
  const iconDim = badgeSize === 'md' ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5'

  return (
    <span
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setHovering(true)}
      onBlur={() => setHovering(false)}
    >
      {/* Children rendered visually disabled — pointer events captured by the overlay */}
      <span
        className="block opacity-50 pointer-events-none select-none"
        aria-disabled="true"
      >
        {children}
      </span>

      {/* Click trap — sits over the child so any click triggers blocked behavior */}
      <button
        type="button"
        onClick={handleBlockedClick}
        aria-label={message}
        className="absolute inset-0 w-full h-full cursor-not-allowed rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400"
      />

      {/* Lock badge */}
      <span
        className={`absolute -top-1 -right-1 ${badgeDim} ${severity.bg} ${severity.text} rounded-full flex items-center justify-center shadow-sm ring-2 ring-white pointer-events-none`}
        aria-hidden="true"
      >
        <HiOutlineLockClosed className={iconDim} />
      </span>

      {/* Tooltip */}
      {hovering && (
        <BlockedTooltip
          side={tooltipSide}
          message={message}
          ctaLabel={ctaLabel}
          severity={severity}
        />
      )}
    </span>
  )
}

function BlockedTooltip({ side, message, ctaLabel, severity }) {
  const sideClasses = {
    top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left:   'right-full top-1/2 -translate-y-1/2 mr-2',
    right:  'left-full top-1/2 -translate-y-1/2 ml-2',
  }
  return (
    <span
      role="tooltip"
      className={`absolute z-50 ${sideClasses[side] || sideClasses.top} w-56 px-3 py-2 rounded-lg border shadow-lg bg-white text-[11px] leading-snug ${severity.pill}`}
    >
      <span className="flex items-start gap-1.5">
        <HiOutlineLockClosed className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span className="flex-1">
          <span className="block font-medium">{message}</span>
          {ctaLabel && (
            <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold opacity-80">
              {ctaLabel} <HiOutlineArrowRight className="w-3 h-3" />
            </span>
          )}
        </span>
      </span>
    </span>
  )
}

function ctaLabelFor(hint) {
  switch (hint) {
    case 'subscribe':       return 'Click to subscribe'
    case 'connect_drive':   return 'Click to connect Drive'
    case 'reconnect_drive': return 'Click to reconnect Drive'
    default:                return null
  }
}

/**
 * BlockedBanner — page-level banner variant for when the entire page's
 * primary action is blocked. Use above a list / form when overlaying every
 * individual button is overkill.
 */
export function BlockedBanner({ action, className = '' }) {
  const gate = useActionGate(action)
  const navigate = useNavigate()
  if (gate.loading || gate.allowed) return null

  const severity = SEVERITY_BADGE[gate.severity] || SEVERITY_BADGE.error
  const ctaLabel = ctaLabelFor(gate.hint_action)

  return (
    <div className={`flex items-start gap-2.5 p-3 rounded-lg border ${severity.pill} ${className}`}>
      <span className={`flex-shrink-0 w-7 h-7 ${severity.bg} ${severity.text} rounded-md flex items-center justify-center`}>
        <HiOutlineLockClosed className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold leading-tight">{gate.message}</p>
        {ctaLabel && gate.cta_url && (
          <button
            type="button"
            onClick={() => navigate(gate.cta_url)}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
          >
            {ctaLabel} <HiOutlineArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  )
}
